import { CheckOutlined, Close, ErrorOutline, Link, LinkOff, SyncOutlined } from "@mui/icons-material";
import { Button, Card, CardContent, Divider, Grid, Typography, Box, LinearProgress } from "@mui/material";
import React, { use, useEffect, useRef, useState } from "react";
import { CerboConnected, FetchMasters, GetLatSyncedDateTime, GetSyncStatus, Login, OAuthConnected, OAuthDisConnect, RunAppointmentSync, RunSync } from "../Service/AxiosService";

type SyncStatus = {
    type: string;
    isRunning: boolean;
    startedAtUtc?: string | null;
    currentStep?: string | null;
    lastCompletedUtc?: string | null;
    lastSuccess?: boolean | null;
    lastMessage?: string | null;
};

const Settings = () => {
    const [isRotating, setIsRotating] = useState(false);
    const [buttonColor, setButtonColor] = useState<"success" | "error" | "warning">("success");
    const [lastSyncedAt, setLastSyncedAt] = useState<any>(null);
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    const [isConnected, setIsConnected] = useState(true); // GHL OAuth connection
    const [cerboConnected, setCerboConnected] = useState<boolean | null>(null); // null = still checking
    const [isSyncing, setIsSyncing] = useState(false);
    const [isApptSyncing, setIsApptSyncing] = useState(false);
    const [syncResult, setSyncResult] = useState<{ type: "success" | "error", text: string } | null>(null);
    const [dataStatus, setDataStatus] = useState<SyncStatus | null>(null);
    const [apptStatus, setApptStatus] = useState<SyncStatus | null>(null);
    const hasFetched = useRef(false);

    // Poll the live sync status so progress (running / current step / last completed) survives a
    // page refresh and reflects background runs (including the 15-min poll), not just clicks here.
    const pollSyncStatus = async () => {
        const res = await GetSyncStatus();
        if (res.success && Array.isArray(res.data)) {
            setDataStatus(res.data.find((s: SyncStatus) => s.type === "Data") || null);
            setApptStatus(res.data.find((s: SyncStatus) => s.type === "Appointment") || null);
        }
    };

    useEffect(() => {
        pollSyncStatus();
        const id = setInterval(pollSyncStatus, 5000);
        return () => clearInterval(id);
    }, []);

    useEffect(() => {
        document.title = `${process.env.REACT_APP_CLINIC_NAME || 'Clinic'} - Settings`;

        if (hasFetched.current) return;
        hasFetched.current = true;
        fetchLastSyncTime();
    }, [lastSyncedAt]);

    useEffect(() => {
        initialLoad();
    }, []);

    var initialLoad = async () => {
        var data = await OAuthConnected();
        if(data.data == true){
            setIsConnected(true);
        }        else{
            setIsConnected(false);
        }
        // Cerbo is API-key auth (no OAuth) — probe it live so a missing/wrong key shows as a red
        // "Not reachable" instead of failing silently on Fetch.
        const cerbo = await CerboConnected();
        setCerboConnected(cerbo.success && cerbo.data === true);
    };

    const fetchLastSyncTime = async (): Promise<void> => {
        const res = await GetLatSyncedDateTime();
        if (res.success) {
            setLastSyncedAt(res.data);
        } else {
            setLastSyncedAt(null);
            // Optionally show error to user
            console.error('Failed to fetch last sync time', res.error);
        }
    };
    
    const handleFetch = async () => {
        setIsRotating(true);
        const res = await FetchMasters();
        setIsRotating(false);
        const msg = (res.success && typeof res.data === "string") ? res.data.toLowerCase() : "";
        if (msg === "sync successfull") {
            setButtonColor("success");
            fetchLastSyncTime();
            setIsInitialLoad(false);
        } else if (msg === "no data fetched") {
            // Endpoint ran fine but nothing came through (e.g. Cerbo/HighLevel not connected yet).
            setButtonColor("warning");
            setIsInitialLoad(false);
        } else {
            setButtonColor("error"); // real failure
            console.error('Error fetching data', res.error || res.data);
        }
    };

    // Manually run the full data sync now (instead of waiting for the 15-min poll).
    const handleSync = async () => {
        setIsSyncing(true);
        setSyncResult(null);
        const res = await RunSync();
        setIsSyncing(false);
        if (res.success) {
            setSyncResult({ type: "success", text: "Data sync started." });
            setTimeout(pollSyncStatus, 800);   // pick up the "running" state promptly
            setTimeout(fetchLastSyncTime, 90000);
        } else {
            setSyncResult({ type: "error", text: "Could not start the data sync. Check the connection and try again." });
        }
    };

    // Manually run the appointment sync now.
    const handleAppointmentSync = async () => {
        setIsApptSyncing(true);
        setSyncResult(null);
        const res = await RunAppointmentSync();
        setIsApptSyncing(false);
        if (res.success) {
            setSyncResult({ type: "success", text: "Appointment sync started." });
            setTimeout(pollSyncStatus, 800);
        } else {
            setSyncResult({ type: "error", text: "Could not start the appointment sync. Check the connection and try again." });
        }
    };

    const formatTime = (iso?: string | null) => {
        if (!iso) return "";
        const d = new Date(iso.endsWith("Z") || iso.includes("+") ? iso : iso + "Z");
        return isNaN(d.getTime()) ? "" : d.toLocaleString();
    };

    const renderStatusRow = (label: string, st: SyncStatus | null) => (
        <Box sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>{label}</Typography>
            {st?.isRunning ? (
                <>
                    <LinearProgress sx={{ borderRadius: 1, height: 6, mb: 0.5 }} />
                    <Typography variant="caption" sx={{ color: "#2563eb" }}>
                        Running{st.currentStep ? ` — ${st.currentStep}` : "…"}{st.startedAtUtc ? ` (started ${formatTime(st.startedAtUtc)})` : ""}
                    </Typography>
                </>
            ) : st?.lastCompletedUtc ? (
                <Typography variant="caption" sx={{ color: st.lastSuccess ? "green" : "red" }}>
                    {st.lastSuccess
                        ? <CheckOutlined sx={{ fontSize: 14, verticalAlign: "middle", mr: 0.5 }} />
                        : <ErrorOutline sx={{ fontSize: 14, verticalAlign: "middle", mr: 0.5 }} />}
                    {st.lastSuccess ? "Completed" : "Failed"} {formatTime(st.lastCompletedUtc)}
                    {!st.lastSuccess && st.lastMessage ? ` — ${st.lastMessage}` : ""}
                </Typography>
            ) : (
                <Typography variant="caption" sx={{ color: "#9ca3af" }}>Not run yet</Typography>
            )}
        </Box>
    );

    const handleConnectionToggle = () => {
        if (!isConnected) {
            login();
        }
        else{
            disconnect();
        }
        setIsConnected(!isConnected);
        // Add your API call here to connect/disconnect
    };

    const login = () => {
        // Genericized OAuth connect (was hardcoded to CHC's EST domain + connector app):
        //  - redirect_uri is derived from THIS deployment's own origin (must be registered in the
        //    GHL connector app's allowed redirect URIs).
        //  - client_id / version_id / scope come from the AA connector app via build-time env vars
        //    (see .env.example). Scope includes calendars.* so the calendar feature can list calendars
        //    and create/update appointments.
        const clientId = process.env.REACT_APP_GHL_CLIENT_ID || '';
        const versionId = process.env.REACT_APP_GHL_VERSION_ID || '';
        // Exactly the 8 scopes the integration actually uses (contacts, custom fields, location,
        // tags, calendars). Conversations scopes were dropped — the backend never calls that API,
        // and requesting a scope the marketplace app doesn't grant fails with "Invalid scope(s)".
        // These must match the scopes enabled on the GHL connector app.
        const scope = process.env.REACT_APP_GHL_SCOPE ||
            'contacts.readonly contacts.write locations/customFields.readonly locations.readonly locations/tags.readonly locations/tags.write calendars.readonly calendars/events.readonly calendars/events.write users.readonly';
        const redirectUri = `${window.location.origin}/Callback`;
        const oauthUrl = 'https://marketplace.gohighlevel.com/oauth/chooselocation?response_type=code'
            + `&redirect_uri=${encodeURIComponent(redirectUri)}`
            + `&client_id=${encodeURIComponent(clientId)}`
            + `&scope=${encodeURIComponent(scope)}`
            + `&version_id=${encodeURIComponent(versionId)}`;
        window.location.href = oauthUrl;
    }
    const disconnect = async () => {
        await OAuthDisConnect();
        setIsConnected(false);
    }
    return (
        <div style={{ backgroundColor: "#F5F6FA"}}>
            <Typography
                sx={{
                    mb: 1,
                    fontWeight: "bold",
                    letterSpacing: 1,
                }}
            >
                Settings
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                <Card sx={{ width: '50%', 
                    borderRadius: 3, 
                    border: '1px solid #dedede', boxShadow:"rgba(21, 39, 88, 0.74) 0px 2px 16px", ml: 2 }}>

                    <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, py: 3 }}>
                            {/* Left - Cerbo side (API-key auth; status probed live) */}
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.75 }}>
                                <Box
                                    sx={{
                                        height: 80,
                                        minWidth: 120,
                                        px: 3,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        borderRadius: 2,
                                        border: '1px solid #dedede',
                                        fontWeight: 700,
                                        fontSize: 20,
                                        color: '#23244A',
                                    }}
                                >
                                    Cerbo
                                </Box>
                                <Typography variant="caption" sx={{ fontWeight: 600, textAlign: 'center', color: cerboConnected === null ? '#9ca3af' : cerboConnected ? '#16a34a' : '#dc2626' }}>
                                    {cerboConnected === null ? 'Checking…' : cerboConnected ? '● Connected' : '● Not reachable — check API keys'}
                                </Typography>
                            </Box>

                            {/* Connection Line with Check/Cross (green only when BOTH sides are up) */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 3 }}>
                                <Box sx={{ width: 20, height: 2, bgcolor: cerboConnected ? '#22c55e' : '#d1d5db' }} />
                                <Box
                                    sx={{
                                        width: 32,
                                        height: 32,
                                        borderRadius: '50%',
                                        bgcolor: (cerboConnected && isConnected) ? '#22c55e' : '#dc2626',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    {(cerboConnected && isConnected) ? (
                                        <CheckOutlined sx={{ color: '#fff', fontSize: 20 }} />
                                    ) : (
                                        <Close sx={{ color: '#fff', fontSize: 20 }} />
                                    )}
                                </Box>
                                <Box sx={{ width: 20, height: 2, bgcolor: isConnected ? '#22c55e' : '#d1d5db' }} />
                            </Box>

                            {/* Right - HighLevel side (OAuth) */}
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.75 }}>
                                <Box
                                    component="img"
                                    src="/GHL.jpg"
                                    alt="HighLevel"
                                    sx={{ height: 70, objectFit: 'contain' }}
                                />
                                <Typography variant="caption" sx={{ fontWeight: 600, textAlign: 'center', color: isConnected ? '#16a34a' : '#dc2626' }}>
                                    {isConnected ? '● Connected' : '● Not connected'}
                                </Typography>
                            </Box>
                        </Box>
                        <Divider sx={{ borderColor: "#aebcef", my: 2 }} />
                        {/* Connect/Disconnect Button */}
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                            <Button
                                variant="contained"
                                color={isConnected ? undefined : "success"}
                                onClick={handleConnectionToggle}
                                sx={{
                                    ...(isConnected && {
                                        bgcolor: '#dc2626',
                                        '&:hover': { bgcolor: '#b91c1c' },
                                    }),
                                    color: '#fff',
                                    textTransform: 'none',
                                    borderRadius: 2,
                                    px: 3,
                                    fontWeight: 500,
                                }}
                                startIcon={isConnected ? <LinkOff /> : <Link />}
                            >
                                {isConnected ? 'Disconnect' : 'Connect'}
                            </Button>
                        </Box>
                    </CardContent>
                </Card>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                <Card sx={{ width: '50%', borderRadius: 3, border: '1px solid #dedede', boxShadow:"rgba(21, 39, 88, 0.74) 0px 2px 16px", ml: 2 }}>
                    <CardContent>
                        <Grid container alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
                            <Typography variant="body1">
                                Fetch Tags and Customfields to the database.
                            </Typography>
                            <Button
                                    variant="contained"
                                    color={buttonColor}
                                    onClick={handleFetch}
                                    startIcon={
                                        <SyncOutlined
                                            className={isRotating ? "rotate" : ""}
                                            sx={{ fontSize: 28 }}
                                        />
                                    }
                                    sx={{
                                        fontWeight: "bold",
                                        textTransform: "none",
                                        boxShadow: 2,
                                        px: 3,
                                    }}
                                >
                                    Fetch Now
                                </Button>
                        </Grid>
                        
                        <Grid>
                            <Typography variant="body2" sx={{ color: "#6b7280", mb: 0.5 }}>
                                Last Fetched:
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {lastSyncedAt && new Date(lastSyncedAt).getFullYear() > 1 ? new Date(lastSyncedAt).toLocaleString() : "Never"}
                            </Typography>
                        </Grid>
                        {buttonColor === "error" && (
                            <Typography variant="body2" sx={{ color: "red", mt: 1 }}>
                                <ErrorOutline sx={{ fontSize: 16, verticalAlign: "middle", mr: 0.5 }} />
                                Error fetching data. Please try again.
                            </Typography>
                        )}
                        {buttonColor === "success" && !isRotating &&  isInitialLoad == false &&(
                            <Typography variant="body2" sx={{ color: "green", mt: 1 }}>
                                <CheckOutlined sx={{ fontSize: 16, verticalAlign: "middle", mr: 0.5 }} />
                                Data fetched successfully.
                            </Typography>
                        )}
                        {buttonColor === "warning" && !isRotating && isInitialLoad == false && (
                            <Typography variant="body2" sx={{ color: "#b45309", mt: 1 }}>
                                <ErrorOutline sx={{ fontSize: 16, verticalAlign: "middle", mr: 0.5 }} />
                                Nothing was fetched — connect Cerbo and HighLevel first.
                            </Typography>
                        )}
                    </CardContent>
                </Card>
                <style>
                    {`
                    .rotate {
                        animation: rotate 1s linear infinite;
                    }
                    @keyframes rotate {
                        100% {
                            transform: rotate(-360deg);
                        }
                    }
                    `}
                </style>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                <Card sx={{ width: '50%', borderRadius: 3, border: '1px solid #dedede', boxShadow: "rgba(21, 39, 88, 0.74) 0px 2px 16px", ml: 2 }}>
                    <CardContent>
                        <Typography variant="body1" sx={{ mb: 0.5 }}>
                            Run a sync now
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#6b7280", mb: 2 }}>
                            Manually trigger a sync instead of waiting for the automatic 15-minute cycle.
                        </Typography>
                        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                            <Button
                                variant="contained"
                                onClick={handleSync}
                                disabled={isSyncing || isApptSyncing || dataStatus?.isRunning || apptStatus?.isRunning || !(cerboConnected && isConnected)}
                                startIcon={<SyncOutlined className={(isSyncing || dataStatus?.isRunning) ? "rotate" : ""} sx={{ fontSize: 24 }} />}
                                sx={{ fontWeight: "bold", textTransform: "none", boxShadow: 2, px: 3 }}
                            >
                                {(isSyncing || dataStatus?.isRunning) ? "Syncing…" : "Sync Data"}
                            </Button>
                            <Button
                                variant="outlined"
                                onClick={handleAppointmentSync}
                                disabled={isSyncing || isApptSyncing || dataStatus?.isRunning || apptStatus?.isRunning || !(cerboConnected && isConnected)}
                                startIcon={<SyncOutlined className={(isApptSyncing || apptStatus?.isRunning) ? "rotate" : ""} sx={{ fontSize: 24 }} />}
                                sx={{ fontWeight: "bold", textTransform: "none", px: 3 }}
                            >
                                {(isApptSyncing || apptStatus?.isRunning) ? "Syncing…" : "Sync Appointments"}
                            </Button>
                        </Box>
                        {!(cerboConnected && isConnected) && (
                            <Typography variant="body2" sx={{ color: "#b45309", mt: 1.5 }}>
                                <ErrorOutline sx={{ fontSize: 16, verticalAlign: "middle", mr: 0.5 }} />
                                Connect both Cerbo and HighLevel before running a sync.
                            </Typography>
                        )}
                        {syncResult && (
                            <Typography variant="body2" sx={{ color: syncResult.type === "success" ? "green" : "red", mt: 1.5 }}>
                                {syncResult.type === "success"
                                    ? <CheckOutlined sx={{ fontSize: 16, verticalAlign: "middle", mr: 0.5 }} />
                                    : <ErrorOutline sx={{ fontSize: 16, verticalAlign: "middle", mr: 0.5 }} />}
                                {syncResult.text}
                            </Typography>
                        )}
                        <Divider sx={{ borderColor: "#e5e7eb", my: 2 }} />
                        {renderStatusRow("Data sync", dataStatus)}
                        {renderStatusRow("Appointment sync", apptStatus)}
                    </CardContent>
                </Card>
            </Box>
        </div>
    );
};

export default Settings;